/**
 * RP4 — Pointer-down / pointer-up handlers extracted from
 * `Scene3DStage.tsx` SceneContent.
 *
 * Drives the spatial-pick + window/crossing-select pipelines:
 *
 *   - Plain click → spatial pick (lifted through the cache-direct
 *     stub-resolver) → topmost candidate → caller dispatches.
 *     SELECTIONCYCLING bit 2 opens the listbox dialog when ≥2
 *     candidates share the cursor's pickbox.
 *
 *   - Drag → window/crossing-select (left-to-right = window, right-to-
 *     left = crossing) → spatial intersect → caller dispatches batch.
 *
 * Locked / frozen layers are filtered out of both pick paths to match
 * AutoCAD's "visible but un-pickable" behaviour for those entities.
 *
 * # Why a dedicated module
 *
 * The original handler closure captured ~12 refs and helper closures
 * across 250 LOC inline in `SceneContent`. Hoisting it to a function
 * with an explicit args bundle makes the wiring visible at a glance
 * and keeps the orchestrator under the 500-LOC budget.
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx`.
 */

import * as THREE from "three";

import type { RendererHandle, SpatialIndex } from "../types";
import { type AdapterContext } from "../adapters";
import {
  typedAnnotativeHidden,
  type TypedEntityResolver,
} from "../adapters/typedFirstRenderable";
import { compareTopmostFirst } from "../raycast/candidateOrdering";
import {
  selectInWindow,
  type SelectionMode,
} from "../raycast/windowSelect";
import {
  isHiddenByLayerChain,
  makeVisibleChildGeometryFilter,
} from "../layerChainVisibility";
import {
  refineEntityPick,
  type RefinedEntityPicker,
} from "./refinedEntityPick";
import type { VisibleSetCache } from "../state/visibleSetCache";
import { useSelectionCyclingStore } from "../../state/selectionCyclingState";
import { useSysvarStore } from "../../dynamicInput/sysvarStore";
import type { Scene3DStageProps } from "../Scene3DStageProps";
import type { EntityPreview } from "@dxf-ai-editor/contracts";
import {
  resolveActiveInputSpace,
  type ActiveInputSpace,
} from "../viewports/activeInputSpace";
import type { ViewportRedirectContext } from "../stageInteractions";
import {
  PICK_APERTURE_PX,
  orthoPixelToWorld as sharedOrthoPixelToWorld,
  pickApertureWorldTolerance,
} from "../stageCameraMath";

/**
 * MC4 S2 — pick/window-select aperture in CSS pixels.
 *
 * `pickToleranceWorld()` defines the paper-space click tolerance as
 * `PICK_APERTURE_PX × pixelToWorld` (4 CSS px). When the pick is REDIRECTED
 * into a paper-space viewport's model view (MSPACE_VIA_VP over an unlocked
 * active VP), the same CSS-pixel aperture must be re-expressed in MODEL
 * units via the VP's model `pixelsPerWorld`, so the hit-test tolerance
 * feels identical to the user regardless of the VP's zoom.
 *
 * `PICK_APERTURE_PX` now lives in `stageCameraMath.ts` (single source
 * shared with the `__edit_dwg_scene` test bridge's `worldTolerance()` —
 * #589 Codex review found the bridge had drifted to a bare `1/zoom`
 * formula that ignored this aperture entirely).
 */

interface InstallStageClickHandlersArgs {
  canvas: HTMLCanvasElement;
  camera: THREE.Camera;
  size: { width: number; height: number };
  handleRef: { current: RendererHandle | null };
  entities: Scene3DStageProps["entities"];
  selectedEntityIds: readonly string[] | undefined;
  onSelectEntity: Scene3DStageProps["onSelectEntity"];
  onSelectEntities: Scene3DStageProps["onSelectEntities"];
  lockedLayersRef: { current: Set<string> };
  hiddenLayersRef: { current: Set<string> };
  visibleSetCacheRef: { current: VisibleSetCache | null };
  buildPreview: (id: string) => EntityPreview | null;
  /** DEFUSE S11 — typed resolver for the typed-first candidate lift (see
   *  `adapters/typedFirstRenderable.ts`). */
  resolveEntity: TypedEntityResolver;
  adapterCtxBase: AdapterContext;
  /**
   * MC4 S2 P2 (iter-4 #1) — the id of the ACTIVE space (model space id or the
   * active layout id), threaded from `Scene3DStage` (`props.activeSpace?.id ??
   * "model"`). Used as the IDENTITY-mode fallback space for both the resolver
   * (`candidateEligible`) AND the dispatched `spaceId`.
   *
   * Why NOT `entities[0]?.spaceId`: when the active layout has NO shell entities
   * (an empty layout) OR the cache-direct path returns the shared empty sentinel
   * (`selectEntitiesInActiveSpace`), `entities[0]` is `undefined` → the fallback
   * silently collapsed to `"model"`. An identity-mode paper-space click then
   * accepted MODEL-space renderables from the shared spatial index (the layout
   * also holds hidden model buckets), making invisible model geometry selectable
   * from paper. The active-space id is authoritative regardless of whether the
   * shell entity list is populated. Optional (defaults to the legacy
   * `entities[0]?.spaceId ?? "model"`) so existing unit-test callers stay valid.
   */
  activeSpaceId?: string;
  /**
   * MC4 S2 — live viewport-redirect context. When present and the redirect
   * condition holds (unlocked active VP in MSPACE_VIA_VP, cursor over the VP
   * rect), pick + drag-window select route through the VP's model space via
   * `resolveActiveInputSpace`: the spatial query runs in VP-local model coords,
   * the tolerance uses the VP model scale, only model-space candidates qualify,
   * and selection dispatches in `spaceId: 'model'`. When `current` is null OR
   * the resolver returns identity (the common case — PSPACE / plain MSPACE /
   * locked VP / cursor outside the VP) every path is byte-equivalent to the
   * legacy paper/model behaviour. Optional so non-VP contexts and tests pass
   * null without churn.
   */
  viewportRedirectRef?: { current: ViewportRedirectContext | null };
  /**
   * cmd-pan — live interactive realtime-pan session flag. When `current` is true
   * a LMB drag PANS the camera (handled by `installStageInteractions`), so this
   * selection handler must NOT fire a click-pick or drag-window select for the
   * same LMB gesture (AutoCAD realtime PAN consumes the LMB exclusively).
   * Optional so non-pan contexts and unit-test callers pass nothing.
   */
  panSessionActiveRef?: { current: boolean };
  /**
   * Codex PR #784 round 7 P2 — OUTLET for the refined picker built from this
   * bundle. On install `.current` is set to a {@link RefinedEntityPicker}
   * closing over exactly the deps `handleClickPick` uses (live spatial index,
   * locked / hidden layer sets, cache-direct lift context) — and, since round 8
   * P2, resolving the same LIVE contextual gates per call through the shared
   * `resolvePickContext`: active-space eligibility, the paper→VP-model redirect
   * and the active viewport's VPLAYER-frozen set; on teardown it is nulled
   * again.
   *
   * Why an outlet rather than a direct call: `ZOOM Object` is handled by
   * `installStageInteractions` (`stageInteractions.ts`), which lives OUTSIDE
   * the R3F canvas and therefore cannot see the `VisibleSetCache`, the typed
   * entity resolver or the adapter context — the three things the cache-direct
   * lift needs. The host (`Scene3DStage` → `SceneContent`) owns one ref, hands
   * it to BOTH installers, and the zoom-object branch then resolves through the
   * SAME picker a click uses instead of the raw bbox-only `SpatialIndex.pick`.
   * Optional: a caller that omits it (jsdom / unit tests, any host without the
   * zoom pick) publishes nothing and the zoom path keeps its documented
   * `SpatialIndex.pick` fallback.
   */
  refinedEntityPickRef?: { current: RefinedEntityPicker | null };
}

/**
 * The CONTEXTUAL gates one pointer position is subject to: which space the
 * input acts in, which candidate spaces that admits, and the ACTIVE viewport's
 * VPLAYER-frozen layer set.
 *
 * Codex PR #784 round 8 P2 — ONE derivation for EVERY entry point that hits the
 * shared spatial index (click-pick, drag-window select, and the published
 * `ZOOM Object` picker). All three used to derive `fallbackSpaceId` /
 * `resolveActiveInputSpace` / `frozenLayers` themselves, and the picker
 * hard-coded them away (`candidateEligible: () => true`, `vpFrozenLayers:
 * null`) on the since-refuted assumption that the main camera dives into the
 * VP's model view in MSPACE_VIA_VP. It does NOT — `viewportContextController`
 * documents "On enter: NO camera change (no dive…)", so the zoom pick's world
 * point is a PAPER point exactly like a click's, and it needs exactly the same
 * gates:
 *
 *   - on a LAYOUT, `SceneBulkLoad` appends the hidden MODEL-space buckets into
 *     the SAME index, so an always-true `candidateEligible` let `ZOOM Object`
 *     zoom to invisible model geometry whose bounds overlap the sheet;
 *   - in MSPACE_VIA_VP, a layer frozen in the active VP (`VPLAYER` /
 *     `ViewportEntity.frozenLayers`) is not drawn through it, so it must not be
 *     pickable there either.
 */
interface ActiveSpaceContext {
  /** The resolved input space (identity, or redirected into the active VP). */
  readonly space: ActiveInputSpace;
  /** Identity-mode candidate space (the ACTIVE space id — see the args doc). */
  readonly fallbackSpaceId: string;
  /** Active VP's VPLAYER-frozen layers; `null` outside a redirected pick. */
  readonly vpFrozenLayers: ReadonlySet<string> | null;
}

/** Args shared by every space-resolving entry point (a subset of the bundle). */
interface ActiveSpaceContextArgs {
  paperPt: { x: number; y: number };
  paperPixelsPerWorld: number;
  activeSpaceId?: string;
  entities: Scene3DStageProps["entities"];
  redirect: ViewportRedirectContext | null;
}

/**
 * Resolve {@link ActiveSpaceContext} for a PAPER-space pointer position.
 *
 * Evaluated PER GESTURE (never captured once): `redirect` is read from the
 * host's live ref at call time, so a layout change, a VP switch, an
 * (un)freeze in the active VP or a PSPACE ↔ MSPACE_VIA_VP transition takes
 * effect immediately for every caller.
 */
function resolveActiveSpaceContext(
  args: ActiveSpaceContextArgs,
): ActiveSpaceContext {
  // iter-4 #1 — derive the fallback from the ACTIVE space id, not entities[0]
  // (undefined on an empty layout / cache-direct empty-sentinel path, which
  // collapsed the fallback to "model" and let a paper click pick invisible model
  // geometry from the shared index). `args.activeSpaceId` is authoritative; the
  // legacy entities[0] derivation stays the default for callers that omit it.
  const fallbackSpaceId =
    args.activeSpaceId ?? args.entities[0]?.spaceId ?? "model";
  const space = resolveActiveInputSpace({
    paperPt: args.paperPt,
    paperPixelsPerWorld: args.paperPixelsPerWorld,
    redirect: args.redirect,
    fallbackSpaceId,
  });
  // MC4 S2 P2 (#D) — the ACTIVE viewport's VPLAYER-frozen layers (only set when
  // the pick is redirected into a VP; identity → no VP frozen set).
  const frozenLayers = space.viewport?.frozenLayers;
  return {
    space,
    fallbackSpaceId,
    vpFrozenLayers:
      frozenLayers && frozenLayers.length > 0 ? new Set(frozenLayers) : null,
  };
}

/** {@link ActiveSpaceContext} plus the point + tolerance a hit-test needs. */
interface PickContext extends ActiveSpaceContext {
  /**
   * `paperPt` mapped into the resolved space and then into the SPATIAL-INDEX
   * frame — i.e. exactly what `refineEntityPick` must query with.
   *
   * The index is uniformly OFFSET-world (`state/spatialBuilder.ts`
   * `buildSpatialRecordsForSpaces` → `applyOffsetToBounds`), but
   * `space.toSpace` is NOT: on the redirected branch it is `paperToModel`,
   * whose output is a DOCUMENT-frame model point. `ActiveInputSpace`
   * `.toOffsetWorld` closes that gap (identity outside a VP, `−O` inside), so
   * an in-VP pick on a survey-scale drawing stops missing by the whole
   * session origin. Nothing downstream needs the space-frame point: both
   * consumers hand this straight to `refineEntityPick`, which returns entity
   * IDS, not coordinates.
   */
  readonly indexWorld: { x: number; y: number };
  /**
   * Hit-test tolerance expressed in the resolved space's units. The origin is
   * a pure TRANSLATION, so this scalar is the same in the index frame.
   */
  readonly toleranceWorld: number;
}

/**
 * Resolve the full single-point pick context. Shared by `handleClickPick` and
 * the published `ZOOM Object` picker so the two can never disagree about which
 * space a pick runs in, which candidates it admits, or how large the pickbox is
 * inside a viewport.
 *
 * Inside a VP the aperture is re-expressed in MODEL units from the VP's model
 * scale (`PICK_APERTURE_PX / space.pixelsPerWorld`) so the tolerance matches
 * the on-screen 4 CSS-px pickbox regardless of the VP's zoom; outside, the
 * caller's paper-space tolerance is used unchanged.
 */
function resolvePickContext(
  args: ActiveSpaceContextArgs & { paperToleranceWorld: number },
): PickContext {
  const ctx = resolveActiveSpaceContext(args);
  return {
    ...ctx,
    indexWorld: ctx.space.toOffsetWorld(ctx.space.toSpace(args.paperPt)),
    toleranceWorld: ctx.space.viewport
      ? PICK_APERTURE_PX / ctx.space.pixelsPerWorld
      : args.paperToleranceWorld,
  };
}

/**
 * Install pointerdown + pointerup listeners on the supplied canvas.
 * Returns a teardown function that removes them.
 */
export function installStageClickHandlers(
  args: InstallStageClickHandlersArgs,
): () => void {
  const { canvas, camera, size } = args;

  function clientToWorld(clientX: number, clientY: number): {
    x: number;
    y: number;
  } {
    const rect = canvas.getBoundingClientRect();
    const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -(((clientY - rect.top) / rect.height) * 2 - 1);
    const v = new THREE.Vector3(ndcX, ndcY, 0).unproject(camera);
    return { x: v.x, y: v.y };
  }

  /**
   * World units per screen pixel for the active ortho camera (the reciprocal
   * of pixels-per-world); `null` for the degenerate non-ortho case. Single
   * source of the frustum math shared by `pickToleranceWorld` (× the CSS-px
   * aperture) and `paperPixelsPerWorld` (its reciprocal). Delegates to the
   * shared `stageCameraMath.orthoPixelToWorld` — kept as a local zero-arg
   * wrapper so the two closure-captured call sites below don't need to
   * thread `camera`/`size.width` through themselves.
   */
  function orthoPixelToWorld(): number | null {
    return sharedOrthoPixelToWorld(camera, size.width);
  }

  function pickToleranceWorld(): number {
    return pickApertureWorldTolerance(camera, size.width, PICK_APERTURE_PX);
  }

  /**
   * MC4 S2 — paper-space camera pixels-per-world (the reciprocal of
   * `orthoPixelToWorld`). Fed to `resolveActiveInputSpace` so the resolver can
   * size the VP model aperture when the caller omits `vpScreenHeightPx`.
   * Returns `1` for the degenerate non-ortho case, keeping the identity path's
   * `pixelsPerWorld` finite.
   */
  function paperPixelsPerWorld(): number {
    const pixelToWorld = orthoPixelToWorld();
    return pixelToWorld !== null && pixelToWorld > 0 ? 1 / pixelToWorld : 1;
  }

  // Drag state lives on the canvas element so it survives this effect's
  // cleanup + re-install cycle. SceneContent's parent re-renders ~50 Hz
  // (props from useEditorStore + selectEntitiesInActiveSpace allocate a
  // fresh array each render); listing those props in `useEffect` deps
  // would tear down and re-install the listeners between pointerdown
  // and pointerup, dropping `dragStart` and silently no-op-ing every
  // click. Stashing on the DOM element decouples drag state from React
  // effect lifecycle entirely.
  type DragStart = {
    x: number;
    y: number;
    clientX: number;
    clientY: number;
  };
  type CanvasWithDragState = HTMLCanvasElement & {
    __edit_dwg_dragStart?: DragStart | null;
  };
  const canvasWithDrag = canvas as CanvasWithDragState;
  const DRAG_THRESHOLD_PX = 4;

  function onPointerDown(e: PointerEvent): void {
    if (e.button !== 0) return;
    // cmd-pan — during a realtime-pan session the LMB drag pans the camera
    // (installStageInteractions), so do NOT arm a selection drag for it.
    if (args.panSessionActiveRef?.current === true) return;
    canvasWithDrag.__edit_dwg_dragStart = {
      x: e.clientX,
      y: e.clientY,
      clientX: e.clientX,
      clientY: e.clientY,
    };
  }

  function onPointerUp(e: PointerEvent): void {
    if (e.button !== 0) return;
    // cmd-pan — never finalise a pick / window-select on a pan-session LMB up.
    if (args.panSessionActiveRef?.current === true) {
      canvasWithDrag.__edit_dwg_dragStart = null;
      return;
    }
    const startedAt = canvasWithDrag.__edit_dwg_dragStart;
    if (!startedAt) return;
    canvasWithDrag.__edit_dwg_dragStart = null;
    const currentHandle = args.handleRef.current;
    if (!currentHandle) return;
    const dx = Math.abs(e.clientX - startedAt.x);
    const dy = Math.abs(e.clientY - startedAt.y);
    const isDrag = dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX;
    const additive = e.shiftKey;

    if (!isDrag) {
      handleClickPick({
        ...args,
        clientToWorld,
        pickToleranceWorld,
        paperPixelsPerWorld,
        clientX: e.clientX,
        clientY: e.clientY,
        additive,
      });
    } else {
      handleDragSelect({
        ...args,
        currentHandle,
        clientToWorld,
        paperPixelsPerWorld,
        startClientX: startedAt.clientX,
        startClientY: startedAt.clientY,
        endClientX: e.clientX,
        endClientY: e.clientY,
        additive,
      });
    }
  }

  // Codex PR #784 round 7 P2 — publish the refined picker for the out-of-canvas
  // callers (`ZOOM Object`).
  //
  // Round 8 P2 — the picker resolves the SAME live contextual gates a click
  // resolves, through the SAME `resolvePickContext` helper: the active-space
  // redirect (`candidateEligible` / `toSpace` / the VP aperture) and the active
  // viewport's VPLAYER-frozen set. It previously hard-coded
  // `candidateEligible: () => true` + `vpFrozenLayers: null`, justified by "the
  // main camera has already been tweened to the VP's model view in
  // MSPACE_VIA_VP" — which is NOT true (`viewportContextController`: "On enter:
  // NO camera change (no dive, no twist, no snapshot)"). The incoming point is
  // therefore a PAPER-space point, exactly like a click's, and without the
  // gates `ZOOM Object` on a layout could zoom to hidden MODEL geometry
  // overlapping the sheet (both live in the ONE shared spatial index) and, in
  // MSPACE_VIA_VP, to geometry frozen in the active viewport.
  //
  // Everything is read at CALL time (`viewportRedirectRef.current`), so a VP
  // switch / freeze / mode change after install is honoured — no stale capture.
  if (args.refinedEntityPickRef) {
    args.refinedEntityPickRef.current = (paperPt, paperToleranceWorld) => {
      const handle = args.handleRef.current;
      if (!handle) return null;
      const pick = resolvePickContext({
        paperPt,
        paperToleranceWorld,
        paperPixelsPerWorld: paperPixelsPerWorld(),
        activeSpaceId: args.activeSpaceId,
        entities: args.entities,
        redirect: args.viewportRedirectRef?.current ?? null,
      });
      return refineEntityPick(pick.indexWorld, pick.toleranceWorld, {
        spatialIndex: handle.spatialIndex,
        lockedLayers: args.lockedLayersRef.current,
        hiddenLayers: args.hiddenLayersRef.current,
        vpFrozenLayers: pick.vpFrozenLayers,
        candidateEligible: pick.space.candidateEligible,
        cache: args.visibleSetCacheRef.current,
        buildPreview: args.buildPreview,
        resolveEntity: args.resolveEntity,
        adapterCtxBase: args.adapterCtxBase,
      }).hit?.id ?? null;
    };
  }

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointerup", onPointerUp);
  return () => {
    canvas.removeEventListener("pointerdown", onPointerDown);
    canvas.removeEventListener("pointerup", onPointerUp);
    if (args.refinedEntityPickRef) args.refinedEntityPickRef.current = null;
  };
}

interface ClickPickArgs extends InstallStageClickHandlersArgs {
  clientToWorld: (cx: number, cy: number) => { x: number; y: number };
  pickToleranceWorld: () => number;
  paperPixelsPerWorld: () => number;
  clientX: number;
  clientY: number;
  additive: boolean;
}

function handleClickPick(args: ClickPickArgs): void {
  const currentHandle = args.handleRef.current;
  if (!currentHandle) return;
  // Plain click → spatial pick → onSelectEntity. PR-I: hit-test
  // skips entities on locked / frozen layers (matches AutoCAD —
  // locked entities are visually visible but cannot be picked).
  const paperPt = args.clientToWorld(args.clientX, args.clientY);
  // MC4 S2 — route the click through the active input space resolver. Over an
  // unlocked active VP (MSPACE_VIA_VP) this redirects the spatial query into
  // the VP's model space; everywhere else it is the IDENTITY space (toSpace is
  // a no-op, candidateEligible always true, viewport null, pixelsPerWorld ==
  // paperPixelsPerWorld), so the paper/model path below is byte-equivalent to
  // the legacy behaviour.
  // Codex PR #784 round 8 P2 — the space / eligibility / VP-frozen derivation is
  // the SHARED `resolvePickContext` (see its doc): the published `ZOOM Object`
  // picker resolves the identical gates from the identical helper, so a pick
  // through either entry point admits exactly the same candidates.
  const pick = resolvePickContext({
    paperPt,
    paperToleranceWorld: args.pickToleranceWorld(),
    paperPixelsPerWorld: args.paperPixelsPerWorld(),
    activeSpaceId: args.activeSpaceId,
    entities: args.entities,
    redirect: args.viewportRedirectRef?.current ?? null,
  });
  const { space, fallbackSpaceId } = pick;
  // Codex PR #784 round 7 P2 — candidate collection + cache-direct lift +
  // child-distance refinement now live in ONE shared picker
  // (`./refinedEntityPick.ts`), so `ZOOM Object` (which used to call the raw
  // `SpatialIndex.pick`, i.e. the bbox broad phase alone) resolves the exact
  // same entity this click does. Every gate that used to be inlined here is
  // documented on that module, step by step.
  const refined = refineEntityPick(pick.indexWorld, pick.toleranceWorld, {
    spatialIndex: currentHandle.spatialIndex,
    lockedLayers: args.lockedLayersRef.current,
    hiddenLayers: args.hiddenLayersRef.current,
    vpFrozenLayers: pick.vpFrozenLayers,
    candidateEligible: space.candidateEligible,
    cache: args.visibleSetCacheRef.current,
    buildPreview: args.buildPreview,
    resolveEntity: args.resolveEntity,
    adapterCtxBase: args.adapterCtxBase,
  });
  const hit = refined.hit;
  const id = hit ? hit.id : null;
  // MC4 S2 — dispatch in the resolved space. Inside a VP that is "model" (the
  // entities live in model space); in the identity case `space.spaceId` IS the
  // `fallbackSpaceId` (= `args.entities[0]?.spaceId ?? "model"`), so this is
  // byte-equivalent to the legacy `args.entities[0]?.spaceId ?? "model"`.
  const spaceId = space.viewport ? space.spaceId : fallbackSpaceId;

  // ── B12 SELECTIONCYCLING — listbox dialog gate ────
  const filteredStacked: ReadonlyArray<{
    id: string;
    type: string;
    layer: string;
  }> =
    // Codex PR #784 round 4 F3 — built from the DISTANCE-REFINED survivors,
    // not from every RBush broad-phase candidate. A sparse INSERT whose large
    // bbox covers the cursor while none of its strokes do (or whose only
    // children the `childFilter` hides) loses the primary pick already; it
    // must not be OFFERED by the cycling dialog either — AutoCAD cycles
    // through the objects the pickbox actually touches. Order is unchanged
    // (topmost-first over the survivors).
    refined.within.length >= 2
      ? [...refined.within]
          .sort(compareTopmostFirst)
          .map((c) => ({
            id: c.id,
            type: c.geometry.kind,
            layer: c.layer,
          }))
      : [];
  const cyclingStore = useSelectionCyclingStore.getState();
  cyclingStore.setStackedCandidates(filteredStacked);
  const selcycle = useSysvarStore.getState().values.SELECTIONCYCLING;
  if (
    hit &&
    filteredStacked.length >= 2 &&
    (selcycle & 2) !== 0 &&
    !args.additive
  ) {
    const rect = args.canvas.getBoundingClientRect();
    cyclingStore.openDialog({
      screen: {
        x: args.clientX - rect.left,
        y: args.clientY - rect.top,
      },
      candidates: filteredStacked,
      spaceId,
    });
    return;
  }

  if (id !== null) {
    args.onSelectEntity(id, spaceId, args.additive);
  } else if (!args.additive) {
    // Empty click clears the selection (AutoCAD parity).
    args.onSelectEntities([], spaceId, false);
  }
}

interface DragSelectArgs extends InstallStageClickHandlersArgs {
  currentHandle: RendererHandle;
  clientToWorld: (cx: number, cy: number) => { x: number; y: number };
  paperPixelsPerWorld: () => number;
  startClientX: number;
  startClientY: number;
  endClientX: number;
  endClientY: number;
  additive: boolean;
}

function handleDragSelect(args: DragSelectArgs): void {
  // Drag → window/crossing select.
  const paperStart = args.clientToWorld(args.startClientX, args.startClientY);
  const paperEnd = args.clientToWorld(args.endClientX, args.endClientY);
  // MC4 S2 — resolve the active input space from the drag's START point (the
  // gesture commits to the VP it began over, mirroring the wheel/pan capture).
  // Identity outside a VP → toSpace is a no-op and candidateEligible is always
  // true, so the model/paper path below is byte-equivalent to the legacy code.
  // Codex PR #784 round 8 P2 — same SHARED derivation the two single-point pick
  // entry points use (`resolveActiveSpaceContext`): active-space fallback,
  // redirect resolution and the active VP's VPLAYER-frozen set, all read live.
  const dragSpace = resolveActiveSpaceContext({
    paperPt: paperStart,
    paperPixelsPerWorld: args.paperPixelsPerWorld(),
    activeSpaceId: args.activeSpaceId,
    entities: args.entities,
    redirect: args.viewportRedirectRef?.current ?? null,
  });
  const { space, fallbackSpaceId } = dragSpace;
  // AutoCAD: left-to-right drag → window mode (fully-inside); right-to-left →
  // crossing (any-touch). The direction is judged in PAPER/screen X (matches
  // the rubber-band overlay drawn in stageInteractions.ts: `e.clientX >=
  // startClientX`) so a VP twist that rotates model coords does not flip the
  // mode.
  const mode: SelectionMode = paperStart.x <= paperEnd.x ? "window" : "crossing";
  // Map the paper rectangle through the active space (no-op outside a VP)
  // before building the axis-aligned model-space rectangle for the spatial
  // query.
  //
  // MC4 S2 P2 — transform ALL FOUR paper corners, not just the two diagonal
  // endpoints. For a VP with a non-zero `viewTwistAngle` the paper rectangle
  // maps to a ROTATED parallelogram in model space; the two off-diagonal
  // corners can fall OUTSIDE the axis-aligned min/max box of the two diagonal
  // corners, so model entities visibly inside the rubber-band would be missed
  // by the spatial prefilter. Taking min/max over all four corners builds an
  // AABB that fully encloses the parallelogram. The spatial query is only an
  // AABB prefilter — a slightly larger AABB is safe because downstream
  // window/crossing containment in `selectInWindow` still applies the precise
  // rubber-band logic. Outside a VP (identity `toSpace`) the four corners are
  // axis-aligned, so this min/max is byte-equivalent to the 2-corner version.
  //
  // ONE-FRAME SEAM — the corners are additionally mapped into the SPATIAL-
  // INDEX frame (`space.toOffsetWorld`). `toSpace` on the redirected branch is
  // `paperToModel`, which yields DOCUMENT-frame model coordinates, while the
  // index is uniformly OFFSET-world (`state/spatialBuilder.ts`). Without this
  // the AABB below brackets nothing on any drawing whose locked origin is
  // non-zero, and an in-VP rubber band selects nothing at all. Identity
  // outside a VP → a no-op, so the paper/model path stays byte-equivalent.
  const corners = [
    { x: paperStart.x, y: paperStart.y },
    { x: paperEnd.x, y: paperStart.y },
    { x: paperEnd.x, y: paperEnd.y },
    { x: paperStart.x, y: paperEnd.y },
  ].map((p) => space.toOffsetWorld(space.toSpace(p)));
  const left = Math.min(...corners.map((c) => c.x));
  const right = Math.max(...corners.map((c) => c.x));
  const top = Math.min(...corners.map((c) => c.y));
  const bottom = Math.max(...corners.map((c) => c.y));
  // MC4 S2 P2 (#C) — for a twisted VP the 4-corner model AABB above only PRE-
  // FILTERS the spatial index; the precise window/crossing containment must run
  // against the actual rubber-band SHAPE, not its enlarged AABB. Otherwise an
  // entity inside the AABB but OUTSIDE the rotated parallelogram (the visible
  // rectangle) would be selected. We refine in PAPER space: map each candidate
  // point back to paper via `space.fromSpace` (modelToPaper) and test against the
  // original axis-aligned paper rectangle (`paperStart`/`paperEnd`). The
  // window/crossing direction stays judged in paper-X (`mode` above). Identity
  // (no VP, or no twist) → omit `refine` so the model AABB IS the test window,
  // byte-equivalent to the legacy single-window path.
  // iter-4 #4 — CLIP the refined paper test window to the active VP's paper
  // rectangle. The drag commits to the VP it STARTED over (resolver keyed off
  // `paperStart`), but the pointer can be RELEASED outside the VP frame. The
  // model render is scissor-clipped to the VP, so a model entity whose
  // `modelToPaper` image lies OUTSIDE the visible VP rect but INSIDE the extended
  // rubber-band is invisible yet would be selected. Intersecting the rubber-band
  // paper rect with the VP paper rect keeps the test window to the visible region
  // (AutoCAD parity: you can only window-select what the VP actually shows). The
  // VP frame is axis-aligned on the sheet (twist only rotates the model view), so
  // a plain rect∩rect is exact. When the drag stays inside the VP the rubber-band
  // is already a subset → the clip is a no-op (no regression for the common case).
  let refine:
    | {
        testWindow: {
          left: number;
          right: number;
          top: number;
          bottom: number;
        };
        mapPoint: (p: { x: number; y: number; z: number }) => {
          x: number;
          y: number;
        };
      }
    | undefined;
  if (space.viewport) {
    const rubberLeft = Math.min(paperStart.x, paperEnd.x);
    const rubberRight = Math.max(paperStart.x, paperEnd.x);
    const rubberTop = Math.min(paperStart.y, paperEnd.y);
    const rubberBottom = Math.max(paperStart.y, paperEnd.y);
    const paperOrigin = args.viewportRedirectRef?.current?.paperOrigin ?? {
      x: 0,
      y: 0,
    };
    const vp = space.viewport;
    const vpCenterX = vp.center.x + paperOrigin.x;
    const vpCenterY = vp.center.y + paperOrigin.y;
    const vpHalfW = vp.width / 2;
    const vpHalfH = vp.height / 2;
    refine = {
      testWindow: {
        left: Math.max(rubberLeft, vpCenterX - vpHalfW),
        right: Math.min(rubberRight, vpCenterX + vpHalfW),
        top: Math.max(rubberTop, vpCenterY - vpHalfH),
        bottom: Math.min(rubberBottom, vpCenterY + vpHalfH),
      },
      // `p` is a candidate GEOMETRY point, i.e. it arrives in the OFFSET-world
      // INDEX frame, while `fromSpace` (`modelToPaper`) expects a point in the
      // ACTIVE space — a DOCUMENT-frame model point on the redirected branch.
      // `fromOffsetWorld` bridges the two (a no-op outside a VP); without it
      // every candidate projected a whole origin away from the paper test
      // window and the refinement rejected everything.
      mapPoint: (p: { x: number; y: number; z: number }) =>
        space.fromSpace(space.fromOffsetWorld({ x: p.x, y: p.y })),
    };
  }
  // PR-I — drop ids whose entity sits on a locked / hidden layer.
  // MC4 S2 — also drop ids the active space rejects (inside a VP only
  // model-space entities qualify; identity → candidateEligible always true).
  const lockedHidden = new Set<string>();
  for (const lid of args.lockedLayersRef.current) lockedHidden.add(lid);
  for (const hid of args.hiddenLayersRef.current) lockedHidden.add(hid);
  // MC4 S2 P2 (#D) — per-VP frozen layers of the active viewport. A model
  // entity on a layer frozen IN THIS VP is invisible through it (the cull path
  // hides it — `viewportCullingAdapter` keys on `vp.frozenLayers`), so it must
  // be un-selectable by an in-VP drag too. Only set when redirected.
  const vpFrozenSet = dragSpace.vpFrozenLayers;
  const rawIds = selectInWindow(
    { left, top, right, bottom },
    mode,
    args.currentHandle.spatialIndex as SpatialIndex,
    refine,
    // Codex PR #784 round 3 P2 — an INSERT wrapper's window / crossing test
    // runs on its CHILDREN; a child hidden by its own layer chain draws
    // nothing, so crossing it must not select the reference and a hidden child
    // OUTSIDE the rubber band must not VETO a WINDOW selection. Same
    // visibility truth as the click path (global off / frozen layers + this
    // VP's frozen set); the candidate gate below only sees the WRAPPER's layer.
    makeVisibleChildGeometryFilter(args.hiddenLayersRef.current, vpFrozenSet),
  );
  const ids = rawIds.filter((entityId) => {
    // MC4 S2 P1 — resolve each candidate from the SPATIAL INDEX, not from
    // `args.entities`. When a layout is active `args.entities` is only the
    // active-space (layout) metadata; the MODEL entities live in the index
    // separately (appended via `modelSpaceEntities` in SceneBulkLoad), so a
    // model id from `rawIds` is ABSENT from `args.entities` → it would default
    // to the layout `fallbackSpaceId` and `candidateEligible('model')` would
    // reject every model hit, making in-VP drag-window select nothing. The
    // click path already resolves via the index (`spatialIndex.resolve(id)`);
    // match it here. The index resolver preserves each renderable's own
    // `spaceId` AND `layer` (model entities carry `"model"` + their real layer).
    const resolved = args.currentHandle.spatialIndex.resolve(entityId);
    const resolvedSpaceId = resolved?.spaceId ?? fallbackSpaceId;
    if (!space.candidateEligible(resolvedSpaceId)) {
      return false;
    }
    // S10d (round-18 FINDING, typedFirstRenderable:127 pick path) — a hidden
    // annotative TEXT/MTEXT/HATCH is not rendered, so a window/crossing drag
    // must not select it via its cache-direct stub either (same predicate the
    // click-pick path + the render gate use).
    const typed = args.resolveEntity(entityId);
    if (typed && typedAnnotativeHidden(typed, args.adapterCtxBase)) {
      return false;
    }
    // MC4 S2 P2 (#B) — resolve each candidate's LAYER from the resolved
    // renderable (the same source the click path / `filterHittableEntities`
    // use), falling back to the `args.entities` metadata only when the id is
    // not in the index. Keying the locked/hidden gate off `args.entities` alone
    // (the previous code) silently admits MODEL entities on a LOCKED/HIDDEN
    // layer while a layout is active, because model ids are absent from
    // `args.entities` (they live in the index, see the spaceId comment above) →
    // the gate read `undefined` and passed them. The resolved renderable's
    // `layer` is authoritative for every candidate regardless of space.
    let layer: string | null = resolved ? resolved.layer : null;
    if (layer === null) {
      const entity = args.entities.find((e2) => e2.id === entityId);
      layer = entity
        ? entity.properties.Layer ?? entity.properties.layer ?? "0"
        : null;
    }
    // Codex PR #784 round 3 P2 — both gates read the candidate's LAYER CHAIN
    // (own layer AND every enclosing block reference's layer,
    // `isHiddenByLayerChain`), exactly like the click path above and
    // `filterHittableEntities`: a legacy-branch block child on an ON layer is
    // still un-selectable by a window / crossing drag while its INSERT's
    // layer is off / locked / frozen in this VP. The chain only exists on a
    // resolved renderable; the metadata fallback has no chain (top level).
    const parentLayers = resolved?.renderParentLayers;
    // MC4 S2 P2 (#D) — drop candidates frozen in the active VP (in-VP only).
    if (
      vpFrozenSet !== null &&
      layer !== null &&
      isHiddenByLayerChain(layer, parentLayers, vpFrozenSet)
    ) {
      return false;
    }
    if (lockedHidden.size === 0) return true;
    // Unknown layer (id absent from both index and metadata) → cannot be on a
    // locked/hidden layer; pass the gate (unchanged contract).
    if (layer === null) return true;
    return !isHiddenByLayerChain(layer, parentLayers, lockedHidden);
  });
  // MC4 S2 — dispatch in the resolved space ("model" inside a VP; the
  // fallbackSpaceId outside, byte-equivalent to the legacy hardcode).
  const spaceId = space.viewport ? space.spaceId : fallbackSpaceId;
  if (
    args.additive &&
    args.selectedEntityIds &&
    args.selectedEntityIds.length > 0
  ) {
    // Union with current selection.
    const merged = Array.from(new Set([...args.selectedEntityIds, ...ids]));
    args.onSelectEntities(merged, spaceId, false);
  } else {
    args.onSelectEntities(ids, spaceId, false);
  }
}
